import {
  MenuListItem,
  Autocomplete,
  IconDelete,
  InlineInput,
} from 'cdk-radial';
import {
  ChangeEvent,
  MutableRefObject,
  forwardRef,
  memo,
  useEffect,
  useRef,
  useState,
  useDeferredValue,
} from 'react';
import { AutocompleteProps } from '../../components/UnifyAdministration/Blocks/types';
import { SimpleTable, SimpleTableRow,SimpleTableBody, SimpleTableData } from 'cdk-radial';
import styled from 'styled-components';

const StyledIllustrationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const getHighlightedText = (text: string, highlight: string) => {
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <span>
      {' '}
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            part.toLowerCase() === highlight.toLowerCase()
              ? { fontWeight: 'bold' }
              : {}
          }
        >
          {part}
        </span>
      ))}{' '}
    </span>
  );
};

const _filterOptions = (
  inputText: string,
  cOptions: string[],
  defaultOptions: any,
  setOptions: Function,
  isLoading: boolean,
  emptySearchMsg: string
) => {
  const results =
    defaultOptions?.length &&
    defaultOptions?.filter(
      (opt: any) => !cOptions.includes(opt.label.toLowerCase())
    );

  if (results?.length) {
    setOptions(results);
  } else {
    if (!isLoading) setOptions([{ label: emptySearchMsg, disabled: true }]);
  }
};

const AutocompleteDropdownWithChip = forwardRef<
  HTMLInputElement,
  AutocompleteProps
>((componentProps, ref) => {
  const {
    className,
    options: defaultOptions,
    minChars = 0,
    autoCompleteProps,
    menuListItemProps,
    inputProps,
    onChipAdded,
    onChipChange,
    chipOptions,
    onChange = () => {},
    onClear = () => {},
    onChangeInput = () => {},
    value,
    dataTestId: parentTestId,
    isLoading,
    emptySearchMsg = 'No Record Found',
    emptyRecordMsg='No Records',
    isHighlightRequired,
  } = componentProps;

  const thisRef = useRef<HTMLInputElement>();
  const dimensionRef = useRef<HTMLDivElement>(null);
  const targetRef = (ref ?? thisRef) as MutableRefObject<HTMLInputElement>;

  const [options, setOptions] = useState(defaultOptions);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value);
  const [inputValue, setInputValue] = useState('');
  const [cOptions, setCOptions] = useState<string[]>(chipOptions || []);

  const deferredValue = useDeferredValue(inputValue);

  const handleRemove = (optionToRemove: string) => {
    const updatedChips = cOptions.filter((opt) => opt !== optionToRemove);
    setCOptions(updatedChips);
    onChipChange?.(updatedChips);
  };

  const handleSelect = (option: any) => {
    if (option.label && !cOptions.includes(option.label)) {
      const updatedChips = [...cOptions, option.label];
      setCOptions(updatedChips);
      onChipAdded?.(option.label);
      onChipChange?.(updatedChips);

      setInputValue('');
      handleClose();
      onChange(option);
    }
    targetRef.current?.focus();
  };

  useEffect(() => {
    // Filter out items already in chips + filter by search text
    const filtered = defaultOptions.filter((opt) => {
      const isNotAChip = !cOptions.includes(opt.label);
      const matchesSearch = opt.label
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return isNotAChip && matchesSearch;
    });

    if (isLoading && inputValue.length === minChars) {
      setOptions([{ label: 'Loading ...', disabled: true } as any]);
    } else {
      setOptions(filtered);
    }
  }, [defaultOptions, inputValue, cOptions, isLoading, minChars]);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const filterOptions = (inputText: string, cOptions: string[]) => {
    _filterOptions(
      inputText,
      cOptions,
      defaultOptions,
      setOptions,
      isLoading as boolean,
      emptySearchMsg
    );
  };

  const findOptions = (event: ChangeEvent<HTMLInputElement>) => {
    const inputText = event.target.value;
    const check = /[^a-zA-Z0-9._@-]/.test(inputText);
    if (check) {
      return;
    }
    setInputValue(inputText);

    if (inputText.length >= minChars) {
      onChangeInput(inputText);
      setSelectedOption({ label: '', value: '' });
      handleOpen();
      filterOptions(inputText, cOptions);
    } else {
      handleClose();
    }
  };

  const handleClear = (event: any) => {
    event.preventDefault();
    handleClose();
    setInputValue('');
    onClear();
    setSelectedOption({ label: '', value: '' });
  };

  const dataTestId = `${parentTestId || 'autocomplete'}-menu`;

  return (
    <>
      <style>
        {`
            div[class^="ChipGroup-"] {
              margin-top:5px;
              display: flex;
              flex-wrap: wrap; 
              align-content: flex-start;
              gap: 4px;
              padding: 2px;
              height: 300px;
              overflow: auto;
            }
            div[class^="ChipOption-"] {
              height:32px;
            }
          `}
      </style>
      <div
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <InlineInput
          name={`${dataTestId}`}
          {...inputProps}
          dataTestId={`${dataTestId}-input`}
          id={`${dataTestId}-input`}
          autoComplete="off"
          labelRef={dimensionRef}
          ref={targetRef}
          value={inputValue}
          onChange={findOptions}
          hasClearButton={selectedOption.label !== ''}
          onInputClear={handleClear}
          onClick={() => {
            if (inputValue.length >= minChars) handleOpen();
          }}
        />

        <div
          style={{
            marginTop: '10px',
            height: '250px',
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            paddingRight: '5px',
            width: '100%',
          }}
        >
          <SimpleTable>
            <SimpleTableBody>
            {cOptions && cOptions.length > 0 ? (
              cOptions.map((option: any, index: number) => (
                <SimpleTableRow key={index}>
                  <SimpleTableData textAlign="left">{option}</SimpleTableData>

                  <SimpleTableData textAlign="right">
                    <IconDelete
                      dataTestId="save-button"
                      iconColor="primary"
                      onClick={() => handleRemove(option)}
                      style={{ marginLeft: '10px', cursor: 'pointer' }}
                    />
                  </SimpleTableData>
                </SimpleTableRow>
              ))
            ) : (
              <SimpleTableRow>
                <SimpleTableData colSpan={2}>
                  <StyledIllustrationContainer>
                    {emptyRecordMsg}
                  </StyledIllustrationContainer>
                </SimpleTableData>
              </SimpleTableRow>
            )}
            </SimpleTableBody>
          </SimpleTable>
        </div>
      </div>
      <Autocomplete
        name="auto-complete"
        dataTestId={dataTestId}
        {...autoCompleteProps}
        isOpen={isOpen}
        onUnselect={selectedOption?.label === ''}
        dimensionRef={dimensionRef}
        labelRef={targetRef}
        onClose={handleClose}
        onChange={findOptions}
        onOpen={handleOpen}
        placement="bottom-start"
        isAuto={false}
        style={{
          zIndex: '100000',
          width: dimensionRef?.current?.offsetWidth,
          maxWidth: dimensionRef?.current?.offsetWidth,
        }}
      >
        {options.length > 0 ? (
          options.map((option, idx) => (
            <MenuListItem
              key={option.label}
              dataTestId={`${dataTestId}-list-item-${idx}`}
              isSelected={selectedOption?.label === option.label}
              isDisabled={option.disabled}
              {...menuListItemProps}
              onClick={() => handleSelect(option)}
              style={{
                zIndex: '1000',
                width: dimensionRef?.current?.offsetWidth,
                maxWidth: dimensionRef?.current?.offsetWidth,
              }}
            >
              {isHighlightRequired &&
                getHighlightedText(option.label, deferredValue)}
              {!isHighlightRequired && option.label}
            </MenuListItem>
          ))
        ) : (
          <MenuListItem isDisabled dataTestId={`${dataTestId}-no-record`}>
            {emptySearchMsg}
          </MenuListItem>
        )}
      </Autocomplete>
    </>
  );
});

AutocompleteDropdownWithChip.defaultProps = {
  inputProps: {},
  autoCompleteProps: {},
  menuListItemProps: {},
  width: 224,
};

AutocompleteDropdownWithChip.displayName = 'AutocompleteDropdownWithChip';

export default memo(AutocompleteDropdownWithChip);
